/**
 * Custom hook for lead form (name + phone + email) submission.
 * Higher-quality leads = better contact success rate.
 */
import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { submitEmail } from '../services/api';
import { analytics } from '../services/analytics';
import { logger } from '../utils/logger';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Strip non-digits and require 10 digits (México mobile/landline)
const sanitizePhone = (raw) => (raw || '').replace(/\D/g, '');
const isValidMexPhone = (raw) => sanitizePhone(raw).length === 10;

export const useEmailSubmit = () => {
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [loading, setLoading] = useState(false);

  const handleChangeField = useCallback((field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    const name = form.name.trim();
    const phoneDigits = sanitizePhone(form.phone);
    const email = form.email.trim();

    if (!name || name.length < 2) {
      toast.error('Por favor ingresa tu nombre');
      return;
    }
    if (!isValidMexPhone(form.phone)) {
      toast.error('Ingresa un teléfono de 10 dígitos (México)');
      return;
    }
    if (!EMAIL_REGEX.test(email)) {
      toast.error('Por favor ingresa un email válido');
      return;
    }

    setLoading(true);
    try {
      const result = await submitEmail(email, name, phoneDigits);
      if (!result.success) {
        throw new Error(result.error || 'Error al enviar');
      }
      analytics.leadCapture('contact_form');
      toast.success(
        `¡Listo, ${name.split(' ')[0]}! 🎯 Te contactaremos pronto.`,
        {
          description: 'Un asesor te llamará o escribirá en 24-48h. Cuando lo haga, CONTESTA. No empieces con el "luego reviso" que ya nos conocemos. 😏',
          duration: 8000
        }
      );
      setForm({ name: '', phone: '', email: '' });
    } catch (error) {
      logger.error('Error submitting lead:', error);
      toast.error('Hubo un error. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [form]);

  return {
    form,
    loading,
    handleSubmit,
    handleChangeField,
  };
};
